import React, { useState } from "react";
import omit from "lodash/omit";
import { Trans, useTranslation } from "react-i18next";
import { AWSConfig } from "@graylog/server-api";
import { BootstrapModalForm, Button, Input } from "components/bootstrap";
import { IfPermitted } from "components/common";
import { ConfigurationsActions } from "stores/configurations/ConfigurationsStore";
import { getValueFromInput } from "util/FormsUtils";
import useProductName from "brand-customization/useProductName";
import { PLUGIN_CONFIG_CLASS_NAME } from "../Constants";
import UserNotification from "../../util/UserNotification";
const _initialState = (config) => omit(config, ["secret_key", "secret_key_salt"]);
const postConfigUpdate = (update) => AWSConfig.updateConfig(update);
const AWSPluginConfiguration = ({
  config = {
    lookups_enabled: false,
    lookup_regions: "us-east-1,us-west-1,us-west-2,eu-west-1,eu-central-1",
    access_key: "",
    secret_key: "",
    proxy_enabled: false
  }
}) => {
  const productName = useProductName();
  const { t } = useTranslation("integrations");
  const [updateConfig, setUpdateConfig] = useState(_initialState(config));
  const [showAwsConfigModal, setShowAwsConfigModal] = useState(false);
  const updateConfigField = (field, value) => {
    setUpdateConfig({ ...updateConfig, [field]: value });
  };
  const onFocusSecretKey = () => {
    setUpdateConfig({ ...updateConfig, secret_key: "" });
  };
  const onUpdate = (field) => (value) => {
    if (typeof value === "object") {
      updateConfigField(field, getValueFromInput(value.target));
    } else {
      updateConfigField(field, value);
    }
  };
  const openModal = () => {
    setShowAwsConfigModal(true);
  };
  const closeModal = () => {
    setShowAwsConfigModal(false);
  };
  const resetConfig = () => {
    setUpdateConfig(_initialState(config));
    closeModal();
  };
  const saveConfig = () => {
    postConfigUpdate(updateConfig).then(
      () => {
        ConfigurationsActions.list(PLUGIN_CONFIG_CLASS_NAME);
        closeModal();
      },
      (error) => {
        UserNotification.error(
          t("aws_plugin_configuration.save_error_message", { error }),
          t("aws_plugin_configuration.save_error_title")
        );
      }
    );
  };
  return /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement("h3", null, t("aws_plugin_configuration.title")), /* @__PURE__ */ React.createElement("p", null, t("aws_plugin_configuration.description")), /* @__PURE__ */ React.createElement("dl", { className: "deflist" }, /* @__PURE__ */ React.createElement("dt", null, t("aws_plugin_configuration.status_instance_detail_lookups")), /* @__PURE__ */ React.createElement("dd", null, config.lookups_enabled === true ? t("aws_plugin_configuration.status_enabled") : t("aws_plugin_configuration.status_disabled")), /* @__PURE__ */ React.createElement("dt", null, t("aws_plugin_configuration.status_connect_through_proxy")), /* @__PURE__ */ React.createElement("dd", null, config.proxy_enabled === true ? t("aws_plugin_configuration.status_enabled") : t("aws_plugin_configuration.status_disabled")), /* @__PURE__ */ React.createElement("dt", null, t("aws_plugin_configuration.status_lookup_regions")), /* @__PURE__ */ React.createElement("dd", null, config.lookup_regions ? config.lookup_regions : t("aws_plugin_configuration.status_not_set")), /* @__PURE__ */ React.createElement("dt", null, t("aws_plugin_configuration.status_access_key")), /* @__PURE__ */ React.createElement("dd", null, config.access_key ? config.access_key : t("aws_plugin_configuration.status_not_set")), /* @__PURE__ */ React.createElement("dt", null, t("aws_plugin_configuration.status_secret_key")), /* @__PURE__ */ React.createElement("dd", null, config.secret_key ? "***********" : t("aws_plugin_configuration.status_not_set"))), /* @__PURE__ */ React.createElement(IfPermitted, { permissions: "clusterconfigentry:edit" }, /* @__PURE__ */ React.createElement(Button, { bsStyle: "info", bsSize: "xs", onClick: openModal }, t("aws_plugin_configuration.edit_configuration"))), /* @__PURE__ */ React.createElement(
    BootstrapModalForm,
    {
      show: showAwsConfigModal,
      title: t("aws_plugin_configuration.modal_title"),
      onSubmitForm: saveConfig,
      onCancel: resetConfig,
      submitButtonText: t("aws_plugin_configuration.submit_configuration")
    },
    /* @__PURE__ */ React.createElement("fieldset", null, /* @__PURE__ */ React.createElement(
      Input,
      {
        id: "aws-lookups-enabled",
        type: "checkbox",
        label: t("aws_plugin_configuration.lookups_enabled_label"),
        help: /* @__PURE__ */ React.createElement("span", null, t("aws_plugin_configuration.lookups_enabled_help")),
        name: "lookups_enabled",
        checked: updateConfig.lookups_enabled,
        onChange: onUpdate("lookups_enabled")
      }
    ), /* @__PURE__ */ React.createElement(
      Input,
      {
        id: "aws-access-key",
        type: "text",
        label: t("aws_plugin_configuration.access_key_label"),
        help: /* @__PURE__ */ React.createElement("span", null, t("aws_plugin_configuration.access_key_help")),
        name: "access_key",
        value: updateConfig.access_key,
        onChange: onUpdate("access_key")
      }
    ), /* @__PURE__ */ React.createElement(
      Input,
      {
        id: "aws-secret-key",
        type: "password",
        label: t("aws_plugin_configuration.secret_key_label"),
        help: /* @__PURE__ */ React.createElement("span", null, t("aws_plugin_configuration.secret_key_help")),
        name: "secret_key",
        value: updateConfig.secret_key !== void 0 ? updateConfig.secret_key : config.secret_key,
        onFocus: onFocusSecretKey,
        onChange: onUpdate("secret_key")
      }
    ), /* @__PURE__ */ React.createElement(
      Input,
      {
        id: "aws-lookup-regions",
        type: "text",
        label: t("aws_plugin_configuration.lookup_regions_label"),
        help: /* @__PURE__ */ React.createElement("span", null, t("aws_plugin_configuration.lookup_regions_help", { productName })),
        name: "lookup_regions",
        value: updateConfig.lookup_regions,
        onChange: onUpdate("lookup_regions")
      }
    ), /* @__PURE__ */ React.createElement(
      Input,
      {
        id: "aws-proxy-enabled",
        type: "checkbox",
        label: t("aws_plugin_configuration.proxy_enabled_label"),
        help: /* @__PURE__ */ React.createElement("span", null, /* @__PURE__ */ React.createElement(
          Trans,
          {
            t,
            i18nKey: "aws_plugin_configuration.proxy_enabled_help",
            values: { productName },
            components: { code: /* @__PURE__ */ React.createElement("code", null), lineBreak: /* @__PURE__ */ React.createElement("br", null), em: /* @__PURE__ */ React.createElement("em", null) }
          }
        )),
        name: "proxy_enabled",
        checked: updateConfig.proxy_enabled,
        onChange: onUpdate("proxy_enabled")
      }
    ))
  ));
};
export default AWSPluginConfiguration;
